"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Upload, Link2, Smartphone, ShoppingCart, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const CUSTOM_SHOWCASE = [
  {
    name: "Custom Wheel Example",
    before: "/showcase/images/before4.jpg",
    wheels: [
      {
        id: "custom-1",
        name: "Custom Style A",
        image: "/showcase/images/after41.webp",
        thumbnail: "/showcase/images/after41.webp",
      },
      {
        id: "custom-2",
        name: "Custom Style B",
        image: "/showcase/images/after42.webp",
        thumbnail: "/showcase/images/after42.webp",
      },
    ],
  },
];

const WHEEL_SOURCES = [
  {
    icon: <ShoppingCart className="h-6 w-6" />,
    title: "Product photos",
    description: "Manufacturer images, retailer product pages, catalog shots-high-quality images work perfectly.",
    examples: "Tire Rack, Fitment Industries, manufacturer sites",
  },
  {
    icon: <Link2 className="h-6 w-6" />,
    title: "Marketplace listings",
    description: "Found wheels on eBay, Craigslist, or Facebook Marketplace? Screenshot or save the image and upload it.",
    examples: "eBay, FB Marketplace, Craigslist, forums",
  },
  {
    icon: <Smartphone className="h-6 w-6" />,
    title: "Phone photos",
    description: "Snap a photo of wheels at a shop, car show, or on another car. Your camera roll works fine.",
    examples: "Showroom shots, car meets, friend's wheels",
  },
  {
    icon: <Upload className="h-6 w-6" />,
    title: "Any image file",
    description: "JPG, PNG, WebP-any common image format. The AI automatically processes and fits the wheel.",
    examples: "Downloaded images, saved photos, screenshots",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Upload your car photo",
    description: "Start with a clear shot of your car. Side profile or 3/4 angle works best.",
  },
  {
    step: "02",
    title: "Upload your wheel image",
    description: "Select 'Custom Wheel' and upload any wheel image. The AI extracts and prepares it automatically.",
  },
  {
    step: "03",
    title: "Generate preview",
    description: "The AI fits the wheel to your car with proper perspective, lighting, and shadows. Download the result.",
  },
];

const CUSTOM_FAQS = [
  {
    question: "What wheel images work best?",
    answer:
      "Clear, well-lit images with the wheel facing forward work best. Product photos from retailers are ideal, but any image where the wheel face is visible will work. The AI handles cropping and background removal automatically.",
  },
  {
    question: "Can I use a screenshot from a website?",
    answer:
      "Yes. Screenshot the wheel from any website-eBay listings, manufacturer pages, forum posts-and upload directly. Make sure the wheel is clearly visible in the screenshot.",
  },
  {
    question: "What if my wheel image has a background?",
    answer:
      "No problem. The AI automatically removes backgrounds and isolates the wheel. You don't need to edit or crop anything before uploading.",
  },
  {
    question: "Can I upload wheels from another car?",
    answer:
      "Absolutely. If you see wheels on another car and want to see them on yours, take a photo of that car's wheels and upload it. The AI extracts the wheel design.",
  },
  {
    question: "Do custom wheel uploads cost extra?",
    answer:
      "No. Custom wheel uploads use the same credits as our wheel library. One preview = one credit, regardless of source.",
  },
  {
    question: "Can I save my custom wheels for later?",
    answer:
      "Yes. Once you upload a custom wheel, it's saved to your wheel library. Reuse it on different car photos without re-uploading.",
  },
];

export function CustomWheelUploadContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Upload Custom Wheels",
    url: "https://thewheelvisualizer.com/custom-wheel-upload",
    description:
      "Upload your own wheel image and preview it on your car. Works with any image source.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
      featureList: "Custom wheel upload, automatic background removal, wheel library",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to preview custom wheels on your car",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload car photo",
        text: "Upload a photo of your car showing the wheels clearly.",
      },
      {
        "@type": "HowToStep",
        name: "Upload wheel image",
        text: "Select custom wheel upload and provide your wheel image from any source.",
      },
      {
        "@type": "HowToStep",
        name: "Generate preview",
        text: "AI processes the wheel and generates a realistic preview on your car.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Upload className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">Any wheel source</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Upload Any Wheel
                <br />
                <span className="text-white/70">See It On Your Car</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Not limited to our library. Upload your own wheel image from any source-product photos, eBay, screenshots, or phone photos-and preview it on your car.
              </p>
              <p className="text-white/80 font-medium mb-8">
                The AI handles everything: cropping, background removal, and realistic fitting.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Any Source</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Auto Process</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Saved for Reuse</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/images/after41.webp"
                  alt="Custom wheel preview"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Works with any wheel image</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported sources */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Wheel sources</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Upload from anywhere
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              Found the perfect wheels online? At a car show? On another car? Grab the image and upload it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {WHEEL_SOURCES.map((source) => (
              <div
                key={source.title}
                className="p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70">
                  {source.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{source.title}</h3>
                <p className="text-white/60 leading-relaxed mb-4">{source.description}</p>
                <p className="text-sm text-white/40">{source.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Process</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              How custom upload works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="relative">
                <div className="text-6xl font-bold text-white/5 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the AI does */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Automatic processing</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Upload messy images. Get clean results.
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                You don&apos;t need to crop, edit, or remove backgrounds. Upload the wheel image as-is-the AI handles everything:
              </p>
              <ul className="space-y-4">
                {[
                  "Background removal and wheel isolation",
                  "Perspective adjustment to match your car",
                  "Color and lighting adaptation",
                  "Realistic shadow and reflection rendering",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/images/after42.webp"
                alt="Custom wheel result"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Example</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Custom wheel results
            </h2>
            <p className="text-white/60 mt-4">
              These previews were created with uploaded wheel images-not from our library.
            </p>
          </div>

          {CUSTOM_SHOWCASE.map((scenario) => (
            <InteractiveShowcase
              key={scenario.name}
              beforeImage={scenario.before}
              wheels={scenario.wheels}
              priority
            />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Custom upload questions
            </h2>
            <p className="text-white/60 mt-4">
              What users ask about uploading their own wheels.
            </p>
          </div>
          <FAQSection faqs={CUSTOM_FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
